import { getDiceServiceUrl, getMediaUrl, isDebug } from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { getManifestData } from "@/helpers/getManifestData";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useDiceSets } from "@/hooks/useDiceSets";
import type { Manifest } from "@/types";
import { useEffect, useState } from "react";

export const useManifests = (
  nodeEnv?: string,
  env?: string,
  gameId?: string,
  stt?: string,
  setId?: string,
) => {
  const [manifests, setManifests] = useState<Record<string, Manifest>>();
  const diceServiceUrl = getDiceServiceUrl(String(nodeEnv), String(env));
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const diceSets = useDiceSets(nodeEnv, env);

  useEffect(() => {
    if (!nodeEnv || !env || !diceSets) return;

    (async () => {
      const data: Record<string, Manifest> = {};
      const setIds: string[] = [];

      if (gameId && stt) {
        try {
          const { data } = await (
            await fetch(
              `${diceServiceUrl}/diceuserconfig/v1/getsetidsincampaign/${gameId}`,
              {
                headers: {
                  Authorization: `Bearer ${stt}`,
                },
              },
            )
          ).json();

          setIds.push(...data);
        } catch (error) {
          if (isDebug) {
            console.error(
              `[DiceCanvases] Error fetching set IDs for game ID "${gameId}":`,
              error,
            );
          }
        }
      } else {
        if (setId) {
          setIds.push(setId);
        }
      }

      await Promise.all(
        setIds.map(async (setId) => {
          const manifestUrl =
            customUrlMap?.[customSetManifestFileName] ||
            diceSets.find(({ id }) => id === setId)?.manifestUrl;

          if (!manifestUrl) {
            if (isDebug) {
              console.warn(
                `[DiceCanvases] Manifest URL for set ID "${setId}" not found.`,
              );
            }

            return;
          }

          const manifestData = await getManifestData({
            mediaUrl,
            setId,
            manifestUrl,
            customUrlMap,
          });

          if (!manifestData) return;

          data[setId] = manifestData;
        }),
      );

      setManifests(data);
      preloadAssets(mediaUrl, Object.values(data), customUrlMap);

      if (isDebug) {
        console.log("[DiceCanvases] Preloading assets complete.");
      }
    })();
  }, [
    customUrlMap,
    diceServiceUrl,
    diceSets,
    env,
    gameId,
    mediaUrl,
    nodeEnv,
    setId,
    stt,
  ]);

  return manifests;
};
